import React from 'react'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'
import autosize from 'autosize'
import debounce from 'lodash/debounce'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import useConversation from 'app/ConversationPage/hooks/useConversation'
import {
  useConversationMessageStore,
  useConversationEntryPointStore,
} from 'stores/conversation'

const ConversationInputBox = () => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext()
  const { ref, onChange, ...userMessageProps } = register('userMessage', {
    required: true,
    maxLength: { value: 4096, message: 'Maksimal 4096 karakter' },
  })
  const userMessage = watch('userMessage')
  const textAreaRef = React.useRef(null)
  const { sendMessage } = useConversation()
  const { isConnecting, lastError, isStreaming } = useConversationMessageStore()
  const { entryPointVariant } = useConversationEntryPointStore()
  const isEntryPointVariantB = React.useMemo(
    () => entryPointVariant === 'variant_2',
    [entryPointVariant]
  )

  const isInputDisabled = React.useMemo(
    () =>
      isConnecting ||
      isStreaming ||
      [
        'daily_quota_exceeded',
        'idle_session_expired',
        'memory_limit_exceeded',
        'global_token_limit_reached',
        'bad_request_error',
      ].includes(lastError),
    [isConnecting, lastError, isStreaming]
  )
  const isSubmitDisabled = React.useMemo(
    () => !userMessage || Boolean(errors?.userMessage),
    [userMessage, errors?.userMessage]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateTextArea = React.useCallback(
    debounce(() => {
      if (textAreaRef?.current) {
        autosize.update(textAreaRef?.current)
      }
    }, 100),
    []
  )

  const isLoading = React.useMemo(
    () => isConnecting || isStreaming,
    [isConnecting, isStreaming]
  )

  React.useEffect(() => {
    if (textAreaRef?.current) autosize(textAreaRef?.current)
  }, [textAreaRef])

  React.useEffect(() => {
    updateTextArea()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMessage])

  return (
    <div className="max-w-xl mx-auto fixed bottom-0 left-0 right-0 p-4 space-y-4 bg-white shadow-[inset_0px_5px_3px_-3px_rgba(0,0,0,0.08)]">
      <div className="flex items-end gap-x-3">
        <Textarea
          {...userMessageProps}
          ref={(e) => {
            ref(e)
            textAreaRef.current = e
          }}
          rows={1}
          value={userMessage}
          isDisabled={isInputDisabled}
          isInvalid={Boolean(errors?.userMessage?.message)}
          errorMessage={errors?.userMessage?.message}
          placeholder={
            isLoading
              ? 'Tunggu jawaban dimuat semua, ya'
              : 'Ketik pesan ke Asisten Guru'
          }
          className="!p-4 !overflow-y-auto min-h-1 max-h-[calc(100vh-180px)]"
          onChange={onChange}
          onKeyDown={(event) => {
            // allow submit Message using "CTRL + ENTER"
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              sendMessage(userMessage)
            }
          }}
          autoFocus={isEntryPointVariantB}
        />
        <div
          className={clsx(
            'pb-2',
            Boolean(errors?.userMessage?.message) && '!pb-6'
          )}
        >
          <Button
            aria-label="sendMessage"
            color="black"
            iconOnly
            disabled={isSubmitDisabled}
            className="!rounded-full !px-1 !overflow-auto"
            onClick={() => {
              sendMessage(userMessage)
            }}
          >
            <Icon color="inverse">arrow_upward</Icon>
          </Button>
        </div>
      </div>
      <div className="text-center">
        <Text variant="body-sm">
          Asisten Guru bisa salah. Selalu periksa lagi, ya.
        </Text>
      </div>
    </div>
  )
}

export default ConversationInputBox
